import { FunctionalComponent } from "preact";
import { useState } from "preact/hooks";

interface ImportExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (format: "json" | "pinboard", filters?: { tag?: string; fromDate?: string; toDate?: string }) => Promise<void>;
  onImport: (data: File | string, type: "json" | "html" | "paste") => Promise<void>;
  availableTags: Array<{ id: number; name: string }>;
}

export const ImportExportModal: FunctionalComponent<ImportExportModalProps> = ({
  isOpen,
  onClose,
  onExport,
  onImport,
  availableTags,
}) => {
  const [activeTab, setActiveTab] = useState<"export" | "import">("export");
  const [exportFormat, setExportFormat] = useState<"json" | "pinboard">("json");
  const [selectedTag, setSelectedTag] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [pasteData, setPasteData] = useState("");
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  if (!isOpen) return null;

  const handleExport = async () => {
    setExporting(true);
    try {
      const filters: { tag?: string; fromDate?: string; toDate?: string } = {};
      if (selectedTag) filters.tag = selectedTag;
      if (fromDate) filters.fromDate = fromDate;
      if (toDate) filters.toDate = toDate;
      
      await onExport(exportFormat, Object.keys(filters).length > 0 ? filters : undefined);
    } finally {
      setExporting(false);
    }
  };

  const handleFileImport = async (e: Event) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const type = file.name.endsWith(".html") ? "html" : "json";
      await onImport(file, type);
      target.value = "";
      setPasteData("");
    } finally {
      setImporting(false);
    }
  };

  const handlePasteImport = async () => {
    if (!pasteData.trim()) return;

    setImporting(true);
    try {
      await onImport(pasteData, "paste");
      setPasteData("");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div class="bg-white dark:bg-stone-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-stone-200 dark:border-stone-800">
        <div class="flex items-center justify-between p-6 border-b border-stone-100 dark:border-stone-800/50 bg-stone-50/50 dark:bg-stone-800/30">
          <h2 class="text-2xl font-display font-bold text-stone-900 dark:text-stone-50">
            Import / Export
          </h2>
          <button
            onClick={onClose}
            class="p-2 rounded-full text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-all"
          >
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="border-b border-stone-100 dark:border-stone-800/50">
          <div class="flex p-1 gap-1">
            <button
              onClick={() => setActiveTab("export")}
              class={`flex-1 px-6 py-3 text-sm font-bold rounded-xl transition-all ${
                activeTab === "export"
                  ? "bg-stone-100 dark:bg-stone-800 text-primary-700 dark:text-primary-400 shadow-sm"
                  : "text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-50 dark:hover:bg-stone-800/50"
              }`}
            >
              Export
            </button>
            <button
              onClick={() => setActiveTab("import")}
              class={`flex-1 px-6 py-3 text-sm font-bold rounded-xl transition-all ${
                activeTab === "import"
                  ? "bg-stone-100 dark:bg-stone-800 text-primary-700 dark:text-primary-400 shadow-sm"
                  : "text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-50 dark:hover:bg-stone-800/50"
              }`}
            >
              Import
            </button>
          </div>
        </div>

        <div class="p-8 overflow-y-auto max-h-[60vh] space-y-8">
          {activeTab === "export" ? (
            <div class="space-y-6">
              <div class="space-y-2">
                <label class="block text-sm font-bold uppercase tracking-widest text-stone-400 dark:text-stone-500">
                  Export Format
                </label>
                <select
                  value={exportFormat}
                  onChange={(e) => setExportFormat((e.target as HTMLSelectElement).value as "json" | "pinboard")}
                  class="w-full px-4 py-3 border border-stone-200 dark:border-stone-800 rounded-xl bg-white dark:bg-stone-950 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all shadow-sm"
                >
                  <option value="json">JSON (pbookmarks format)</option>
                  <option value="pinboard">Pinboard JSON</option>
                </select>
              </div>

              <div class="pt-6 border-t border-stone-100 dark:border-stone-800/50 space-y-6">
                <h3 class="text-xs font-bold uppercase tracking-widest text-stone-400 dark:text-stone-500">
                  Filters (Optional)
                </h3>

                <div class="space-y-4">
                  <div class="space-y-2">
                    <label class="block text-sm font-semibold text-stone-700 dark:text-stone-300">
                      Filter by Tag
                    </label>
                    <select
                      value={selectedTag}
                      onChange={(e) => setSelectedTag((e.target as HTMLSelectElement).value)}
                      class="w-full px-4 py-3 border border-stone-200 dark:border-stone-800 rounded-xl bg-white dark:bg-stone-950 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all shadow-sm"
                    >
                      <option value="">All tags</option>
                      {availableTags.map((tag) => (
                        <option key={tag.id} value={tag.name}>
                          {tag.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div class="grid grid-cols-2 gap-4">
                    <div class="space-y-2">
                      <label class="block text-sm font-semibold text-stone-700 dark:text-stone-300">
                        From Date
                      </label>
                      <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate((e.target as HTMLInputElement).value)}
                        class="w-full px-4 py-3 border border-stone-200 dark:border-stone-800 rounded-xl bg-white dark:bg-stone-950 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all shadow-sm"
                      />
                    </div>
                    <div class="space-y-2">
                      <label class="block text-sm font-semibold text-stone-700 dark:text-stone-300">
                        To Date
                      </label>
                      <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate((e.target as HTMLInputElement).value)}
                        class="w-full px-4 py-3 border border-stone-200 dark:border-stone-800 rounded-xl bg-white dark:bg-stone-950 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all shadow-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="bg-primary-50/50 dark:bg-primary-900/10 p-5 rounded-2xl border border-primary-100 dark:border-primary-900/30">
                <p class="text-sm text-primary-800 dark:text-primary-300 font-serif text-lg leading-relaxed">
                  {selectedTag || fromDate || toDate
                    ? `Exporting filtered bookmarks${selectedTag ? ` with tag "${selectedTag}"` : ""}${fromDate || toDate ? " in date range" : ""}.`
                    : exportFormat === "json"
                    ? "Export all bookmarks in standard JSON format for backup or portability."
                    : "Export all bookmarks in Pinboard-compatible JSON format."}
                </p>
              </div>

              <button
                onClick={handleExport}
                disabled={exporting}
                class="w-full px-6 py-3.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:bg-stone-300 dark:disabled:bg-stone-800 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 font-bold shadow-md hover:shadow-lg"
              >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {exporting ? "Exporting..." : "Download Export"}
              </button>
            </div>
          ) : (
            <div class="space-y-8">
              <div class="space-y-3">
                <label class="block text-sm font-bold uppercase tracking-widest text-stone-400 dark:text-stone-500">
                  Upload File
                </label>
                <input
                  type="file"
                  accept=".json,.html"
                  onChange={handleFileImport}
                  disabled={importing}
                  class="w-full px-4 py-3 border border-stone-200 dark:border-stone-800 rounded-xl bg-white dark:bg-stone-950 text-stone-900 dark:text-stone-100 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-bold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 dark:file:bg-primary-900 dark:file:text-primary-300 transition-all shadow-sm"
                />
                <p class="text-xs text-stone-400 dark:text-stone-500 italic">
                  Supports Pinboard JSON, standard JSON, or Netscape HTML (Chrome/Firefox export)
                </p>
              </div>

              <div class="flex items-center gap-4">
                <div class="flex-1 border-t border-stone-100 dark:border-stone-800/50"></div>
                <span class="text-xs font-bold text-stone-300 dark:text-stone-600 uppercase tracking-widest">OR</span>
                <div class="flex-1 border-t border-stone-100 dark:border-stone-800/50"></div>
              </div>

              <div class="space-y-3">
                <label class="block text-sm font-bold uppercase tracking-widest text-stone-400 dark:text-stone-500">
                  Paste Pinboard JSON
                </label>
                <textarea
                  value={pasteData}
                  onInput={(e) => setPasteData((e.target as HTMLTextAreaElement).value)}
                  placeholder='{"bookmarks": [...]}'
                  disabled={importing}
                  class="w-full px-4 py-3 border border-stone-200 dark:border-stone-800 rounded-xl bg-white dark:bg-stone-950 text-stone-900 dark:text-stone-100 h-40 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all shadow-sm"
                />
              </div>

              <button
                onClick={handlePasteImport}
                disabled={importing || !pasteData.trim()}
                class="w-full px-6 py-3.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:bg-stone-300 dark:disabled:bg-stone-800 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 font-bold shadow-md hover:shadow-lg"
              >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                {importing ? "Importing..." : "Import from Paste"}
              </button>

              <div class="bg-amber-50/50 dark:bg-amber-900/10 p-5 rounded-2xl border border-amber-100 dark:border-amber-900/30">
                <p class="text-sm text-amber-800 dark:text-amber-300 font-serif text-lg leading-relaxed">
                  Duplicate URLs will be automatically skipped. The import will show you how many bookmarks were imported and skipped.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
